/** @file Configuration model for select components Provides default settings and configuration options */

import { HxSelectFilterFn, HxSelectPosition, HxSelectSize } from './select-types';

/**
 * Configuration options for select components
 *
 * @template T The type of option values
 */
export type HxSelectConfig<T = any> = {
  /**
   * Default size for select components
   *
   * @default 'md'
   */
  defaultSize: HxSelectSize;

  /**
   * Default dropdown position
   *
   * @default 'bottom-start'
   */
  defaultPosition: HxSelectPosition;

  /**
   * Default placeholder text
   *
   * @default 'Select an option'
   */
  defaultPlaceholder: string;

  /**
   * Default "no results" message for search
   *
   * @default 'No results found'
   */
  noResultsMessage: string;

  /**
   * Whether to enable search by default
   *
   * @default false
   */
  enableSearch: boolean;

  /**
   * Whether to enable virtual scrolling by default
   *
   * @default false
   */
  enableVirtualScroll: boolean;

  /**
   * Default item height for virtual scrolling
   *
   * @default 40
   */
  virtualScrollItemHeight: number;

  /**
   * Threshold for enabling virtual scroll automatically
   *
   * @default 100
   */
  virtualScrollThreshold: number;

  /**
   * Type-ahead search timeout in milliseconds
   *
   * @default 300
   */
  typeAheadDebounce: number;

  /**
   * Whether to close dropdown on selection (single-select only)
   *
   * @default true
   */
  closeOnSelect: boolean;

  /** Custom filter function for search */
  filterFn?: HxSelectFilterFn<T>;

  /**
   * Maximum number of selections allowed (multi-select)
   *
   * @default undefined (no limit)
   */
  maxSelections?: number;

  /**
   * Whether to show "Select All" option in multi-select
   *
   * @default false
   */
  showSelectAll: boolean;
};

/** Default configuration for select components */
export const DEFAULT_SELECT_CONFIG: HxSelectConfig = {
  defaultSize: 'md',
  defaultPosition: 'bottom-start',
  defaultPlaceholder: 'Select an option',
  noResultsMessage: 'No results found',
  enableSearch: false,
  enableVirtualScroll: false,
  virtualScrollItemHeight: 40,
  virtualScrollThreshold: 100,
  typeAheadDebounce: 300,
  closeOnSelect: true,
  showSelectAll: false,
};

/** Helper to create custom config with defaults */
export function createSelectConfig<T>(overrides?: Partial<HxSelectConfig<T>>): HxSelectConfig<T> {
  return {
    ...DEFAULT_SELECT_CONFIG,
    ...overrides,
  };
}
